import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  Paper,
  InputAdornment,
  Backdrop,
  Container,
  Card,
  CircularProgress,
  Stack,
  IconButton,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TablePagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { getTeamMembersWithoutSearchAndInActiveMembers } from "src/DAL/teamMembers";
import { useSnackbar } from "notistack";
import CheckPrivilege from "src/components/generalComponents/CheckPrivilege";
import { Page, Scrollbar, SearchNotFound } from "src/components";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "src/hooks";
import { Icon } from "@iconify/react/dist/iconify.js";
import {
  calculate_fine,
  fine_summary,
  update_employee_fine,
} from "src/DAL/fine";
import ListHead from "src/components/ListHead";
import {
  getQueryParam,
  manageQueryParams,
} from "src/components/ManageQueryParams";
import FineFilter from "./components/FineFilter";
import ReactFilterChips from "react-filter-chips";
import ExpenceCard from "../expense/ExpenceCard";
import ListToolbar from "src/components/ListToolbar";
import MoreMenu from "src/components/MoreMenu";
import CustomConfirmation from "src/components/Confirmation";

// import your APIs

const TABLE_HEAD = [
  { id: "emp_id", label: "#", alignRight: false },
  { id: "emp_name", label: "Employee Name", alignRight: false },
  { id: "date", label: "Month", alignRight: false },
  { id: "prev_fine", label: "Prev Fine", alignRight: false },
  { id: "fine", label: "Late Minutes", alignRight: false },
  { id: "due_fine", label: "Current Fine", alignRight: false },
  { id: "total_fine", label: "Chargeable Fine", alignRight: false },
  { id: "total_fine", label: "Discount", alignRight: false },
  { id: "total_fine", label: "Final Fine", alignRight: false },
];

export default function FineSummary() {
  const { _get_user_profile } = useAppContext();
  const navigate = useNavigate();
  const profile = _get_user_profile();
  const { enqueueSnackbar } = useSnackbar();
  const [sidebarPermissions, setSidebarPermissions] = useState();

  const [allEmployee, setAllEmployee] = useState([]);
  const [employee, setEmployee] = useState(getQueryParam("employee"));

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [count, setCount] = useState(0);
  const [filterName, setFilterName] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState(filterName);

  const [openBackdrop, setOpenBackdrop] = useState(false);

  // filter states

  const now = moment();
  // Query params (if available)
  const startFromQuery = getQueryParam("start_date");
  const endFromQuery = getQueryParam("end_date");
  // let fiscalStart, fiscalEnd;
  // const year = now.month() >= 6 ? now.year() : now.year() - 1;
  // fiscalStart = moment({ year, month: 6, day: 1 }); // July 1
  // fiscalEnd = now;

  const fiscalStart = moment().startOf("month");
  const fiscalEnd = moment().endOf("month");

  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
  const [filterData, setFilterData] = useState({
    date_from: startFromQuery ? moment(startFromQuery) : fiscalStart,
    date_to: endFromQuery ? moment(endFromQuery) : fiscalEnd,
  });
  const [dateChangeTrigger, setDateChangeTrigger] = useState(false); // to trigger useEffect when date changes
  const [filterChipData, setFilterChipData] = useState(null);

  const [openDiscountModal, setOpenDiscountModal] = useState(false);
  const [targetFine, setTargetFine] = useState(null);

  // -------------------- HANDLE SEARCH --------------------
  const handleSubmit = async () => {
    if (!filterData.date_from || !filterData.date_to) {
      enqueueSnackbar("Please select starting and ending month!", {
        variant: "warning",
      });
      return;
    }

    const postData = {
      employee: filterData?.employee?._id || null,
      company: filterData?.company || null,
      start_date: moment(filterData?.date_from)
        .startOf("month")
        .format("YYYY-MM-DD"),
      end_date: moment(filterData?.date_to).endOf("month").format("YYYY-MM-DD"),
    };

    try {
      setLoading(true);

      const resp = await fine_summary(page, rowsPerPage, filterName, postData);
      if (resp.code === 200) {
        let employess_list = resp?.data?.payroll_array || [];
        setResult({
          employees: [...employess_list],
          all_employees: [...employess_list],
          totalActualFine: resp?.data?.total_fine || 0,
          totalDeductedFine: resp?.data?.total_chargeable_fine || 0,
        });

        setCount(resp?.data?.total_count || 0);
        manageQueryParams({
          // employee: postData.employee || "",
          // company: postData?.company?._id || "",
          start_date: postData.start_date,
          end_date: postData.end_date,
        });
      } else {
        enqueueSnackbar(resp.message, { variant: "error" });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/fine");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    const value = event.target.value;
    setFilterName(value);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilter(filterName);
      setPage(0);
    }, 1200);

    return () => clearTimeout(timer);
  }, [filterName]);

  useEffect(() => {
    setSidebarPermissions(profile?.privileges);
  }, [profile?.privileges]);

  useEffect(() => {
    if (filterData.date_from && filterData.date_to) {
      handleSubmit();
    }
  }, [filterData, page, rowsPerPage, debouncedFilter]);

  const handleUpdateFine = async () => {
    const resp = await calculate_fine();
    if (resp.code === 200) {
      enqueueSnackbar(resp.message, { variant: "success" });
      handleSubmit();
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
  };

  const cardData = () => {
    const list = [
      {
        color: "#ffff",
        title: `Total Fine`,
        count: result?.totalActualFine ? result?.totalActualFine : 0,
        icon: "game-icons:money-stack",
      },
      {
        color: "#ffff",
        title: `Deducted Fine`,
        count: result?.totalDeductedFine ? result?.totalDeductedFine : 0,
        icon: "game-icons:money-stack",
      },
    ];

    return list;
  };

  const handleFilterClear = () => {
    setFilterChipData(null);
    setFilterData({
      date_from: fiscalStart,
      date_to: fiscalEnd,
    });
  };

  const memuOptions = (row) => {
    const MENU_OPTIONS = [];

    MENU_OPTIONS.unshift({
      label: "Apply Discount",
      icon: "ic:baseline-minus",
      color: "var(--clr-green)",
      handleClick: () => {
        handleOpenDiscountModal(row);
      },
    });

    return MENU_OPTIONS;
  };

  const handleOpenDiscountModal = (data) => {
    setOpenDiscountModal(true);
    setTargetFine({
      ...data,
      discount_amount: data.fine_discount || 0,
    });
  };

  const confirmAdminDiscount = async () => {
    const resp = await update_employee_fine({
      emp_obj_id: targetFine?.employee_id,
      attendance_id: targetFine?.attendance_id,
      fine_discount: targetFine?.discount_amount || 0,
      final_chargeable_fine:
        targetFine?.current_month_total_chargeable_fine -
        (targetFine?.discount_amount || 0),
    });
    if (resp.code === 200) {
      enqueueSnackbar(resp.message, { variant: "success" });
      setOpenDiscountModal(false);
      handleSubmit();
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
  };
  return (
    <>
      {sidebarPermissions && (
        <CheckPrivilege module="fine_sheet" variable="add" />
      )}

      {openDiscountModal && (
        <CustomConfirmation
          open={openDiscountModal}
          setOpen={setOpenDiscountModal}
          title={`Are you sure you want to apply a discount to ${targetFine?.employee_name}'s fine?`}
          handleAgree={confirmAdminDiscount}
          renderElement={
            <div>
              <Typography variant="subtitle1" gutterBottom className="pb-2">
                Chargeable Fine:{" "}
                {targetFine?.current_month_total_chargeable_fine || 0}
              </Typography>
              <TextField
                label="Discount Amount *"
                type="number"
                fullWidth
                name="discount_amount"
                value={targetFine?.discount_amount || ""}
                onKeyDown={(e) => {
                  if (["e", "+", "-", "_", "E"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  const max = targetFine?.current_month_total_chargeable_fine;
                  if (value > max) {
                    return;
                  }
                  setTargetFine((prev) => ({
                    ...prev,
                    discount_amount: value,
                  }));
                }}
                onWheel={(e) => e.target.blur()}
                max={targetFine?.current_month_total_chargeable_fine}
                min={0}
              />

              <Typography variant="subtitle1" gutterBottom className="pt-2">
                Final Chargeable Fine After Discnount:{" "}
                {targetFine?.current_month_total_chargeable_fine -
                  (targetFine?.discount_amount || 0)}
              </Typography>
            </div>
          }
        />
      )}

      <FineFilter
        openFilterDrawer={openFilterDrawer}
        setOpenFilterDrawer={setOpenFilterDrawer}
        filterData={filterData}
        setFilterData={(data) => {
          setFilterData(data);
          setPage(0);
        }}
        setFilterChipData={setFilterChipData}
        filterName={"Fine Filter"}
        handleFilterClear={handleFilterClear}
      />

      <Page title="Fine Summary">
        {sidebarPermissions?.fine_sheet?.view && (
          <>
            <Backdrop
              sx={{
                background: "rgba(255, 255, 255, 0.7)",
                zIndex: (theme) => theme.zIndex.drawer + 1,
              }}
              open={openBackdrop || loading}
            >
              <CircularProgress color="primary" />
            </Backdrop>

            <Container maxWidth="xl">
              <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                {/* Heading */}
                <Grid item xs={12} md="auto">
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "start",
                    }}
                  >
                    Fine Summary
                  </Typography>
                </Grid>

                {/* Right Section */}
                <Grid
                  item
                  xs={12}
                  md
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: {
                      xs: "flex-start",
                      md: "flex-end",
                    },
                    gap: 1.5,
                  }}
                >
                  {cardData().map((item, i) => (
                    <Box
                      key={i}
                      sx={{
                        minWidth: {
                          xs: "100%",
                          sm: 220,
                        },
                        maxWidth: 300,
                        flex: 1,
                      }}
                    >
                      <ExpenceCard
                        color={item.color}
                        title={item.title}
                        count={item.count}
                        icon={item.icon}
                      />
                    </Box>
                  ))}
                </Grid>
              </Grid>
              <Card
                sx={{
                  boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
                  mb: 1,
                }}
              >
                <Box px={2}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="h6" sx={{ fontWeight: "500" }}>
                      Employee Fine Details
                    </Typography>
                    <div className="d-flex align-items-center">
                      <Button
                        variant="contained"
                        onClick={handleUpdateFine}
                        startIcon={
                          <Icon icon="streamline-freehand:calculator-calculator-app" />
                        }
                        sx={{ mr: 2 }}
                      >
                        Update Fine
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<Icon icon="mdi:filter" />}
                        onClick={() => setOpenFilterDrawer(true)}
                      >
                        Filter
                      </Button>
                      <ListToolbar
                        filterName={filterName}
                        onFilterName={handleFilterByName}
                        moduleName=""
                      />
                    </div>
                  </Stack>

                  {/* TABLE */}
                  <Scrollbar>
                    {filterChipData && (
                      <div className="">
                        <ReactFilterChips
                          filterData={filterChipData}
                          tempState={filterChipData}
                          emptyFilter={filterChipData}
                          clearLabel="Reset Filter"
                          filterLabel="Filter By"
                          onDeleteChip={(obj, targetValue) => {
                            const key = targetValue?.key;
                            setFilterData((prev) => {
                              const updated = { ...prev };
                              if (key === "dates") {
                                updated.date_from = fiscalStart;
                                updated.date_to = fiscalEnd;
                              } else {
                                delete updated[key];
                              }

                              return updated;
                            });

                            setFilterChipData((prev) => {
                              const updated = { ...prev };
                              delete updated[key];
                              return updated;
                            });
                          }}
                          onClear={(e) => {
                            handleFilterClear();
                          }}
                        />
                      </div>
                    )}
                    <TableContainer sx={{ minWidth: 800 }}>
                      <Table>
                        <ListHead headLabel={TABLE_HEAD} />
                        <TableBody>
                          {result?.employees.map((row, index) => {
                            return (
                              <TableRow hover key={index} tabIndex={-1}>
                                <TableCell>
                                  <Typography variant="subtitle2" noWrap>
                                    {page * rowsPerPage + index + 1}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2" noWrap>
                                    {row?.employee_name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    color="text.secondary"
                                  >
                                    {row?.employee?.email}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2" noWrap>
                                    {row.month_number && row.year
                                      ? moment(
                                          `${row.month_number}-${row.year}`,
                                          "M-YYYY",
                                        ).format("MMMM YYYY")
                                      : "N/A"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2" noWrap>
                                    Rs.{" "}
                                    {row?.previous_month_final_chargeable_fine ||
                                      0}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2" noWrap>
                                    {row?.current_month_total_late_minutes || 0}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2" noWrap>
                                    Rs. {row?.current_month_total_fine || "0"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2" noWrap>
                                    Rs.{" "}
                                    {row?.current_month_total_chargeable_fine ||
                                      "0"}
                                  </Typography>
                                </TableCell>

                                <TableCell>
                                  <Typography variant="subtitle2" noWrap>
                                    Rs.
                                    {row.fine_discount || 0}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2" noWrap>
                                    Rs.{row.current_month_final_chargeable_fine}
                                  </Typography>
                                </TableCell>
                                {!row.fine_deducted &&
                                row.current_month_final_chargeable_fine > 0 ? (
                                  <TableCell>
                                    <Typography variant="subtitle2" noWrap>
                                      <MoreMenu
                                        menu={memuOptions(row)}
                                        data={row}
                                      />
                                    </Typography>
                                  </TableCell>
                                ) : (
                                  ""
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>

                        {(!result?.employees ||
                          result?.employees?.length == 0) && (
                          <TableBody>
                            <TableRow>
                              <TableCell
                                align="center"
                                colSpan={TABLE_HEAD.length}
                                sx={{ py: 3 }}
                              >
                                <SearchNotFound searchQuery={filterName} />
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        )}
                      </Table>
                    </TableContainer>
                  </Scrollbar>

                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={count}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    title="Fine Summary"
                  />
                </Box>
              </Card>
            </Container>
          </>
        )}
      </Page>
    </>
  );
}
